// React Compiler opt-out: this file still reads mutable external state during render
// (legacy store object getters and/or current-time reads) that the useFluxStore bridge
// doesn't cover. Remove after migrating those reads (see flux-bridge-conversion skill).
'use no memo';

import {Chip, Icon, Text} from '@albert-io/atomic';
import {useDroppable} from '@dnd-kit/core';
import cx from 'classnames';
import {useShallow} from 'zustand/react/shallow';
import React, {useCallback, useContext, useEffect, useEffectEvent, useMemo} from 'react';
import {ItemBankRefsContext} from './ItemBankRefsContext';
import classNames from 'classnames';
import type {QuestionTypeMap} from 'client/components/QuestionTypes/V2/useQuestionStores';
import {MovableItem} from '../../shared/MovableItem.react';
import {getSmallSupplement, SupplementContent} from '../../shared/DraggableChip';
import {isSupplement} from '../../shared/utils';
import {itemsShareValue} from '../../shared/interchangeableItems';
import {noAnswerText, useGradedItemCopy} from '../../shared/gradedItemCopy';
import {ANNOTATE_IGNORE_PROPS} from 'client/components/PracticeView/Annotations/engine/annotationTextProjection';
import {useIsAnnotationModeActive} from 'client/components/PracticeView/Annotations/mode/useIsAnnotationModeActive';
import {lockedControlProps} from 'client/components/PracticeView/Annotations/shared/annotationFreeze';
import {useItemMoveCopy} from '../../shared/itemMoveCopy';
import {useBlankName} from './announcements';

/** Focus lands on the slot control, but the ring goes around the whole blank, prompt included. */
const BLANK_FOCUS_RING_CLASS =
  'has-[>[role=button]:focus-visible]:outline-3 has-[>[role=button]:focus-visible]:outline-offset-3 has-[>[role=button]:focus-visible]:outline-border-selected';

interface DroppableItemProps {
  slot: {id: string; label?: string};
  /** The slot's position among the question's slots, 0-based. */
  index: number;
  /** Flashes the blank to show where a placed item landed, in place of the fly-in. */
  isHighlighted?: boolean;
  /** Whether an item with the same value as the slot's answer counts as correct. */
  interchangeable: boolean;
  useStoreHook: QuestionTypeMap['item-bank'];
  onClick: () => void;
  onItemClick: (itemId: string, e?: React.SyntheticEvent) => void;
  onResetItem?: (itemId: string) => void;
  registerValueRenderer?: (slotId: string, el: HTMLDivElement | null) => void;
}

export const DroppableItem = ({
  slot,
  index,
  isHighlighted = false,
  interchangeable,
  useStoreHook,
  onClick,
  onItemClick,
  onResetItem,
  registerValueRenderer
}: DroppableItemProps) => {
  const {isOver, setNodeRef, node} = useDroppable({id: slot.id});
  const {registerSlotEl} = useContext(ItemBankRefsContext);
  const isAnnotationModeActive = useIsAnnotationModeActive();

  // oxlint-disable-next-line react/hooks -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  const {activeItemId, isDragging, isSubmitted, correctAnswer, wordBankItems} = useStoreHook(
    useShallow((state) => ({
      dropSlots: state.dropSlots,
      activeItemId: state.activeItemId,
      isDragging: state.isDragging,
      isSubmitted: state.isSubmitted,
      correctAnswer: state.correctAnswer,
      wordBankItems: state.wordBank
    }))
  );

  const gradedCopy = useGradedItemCopy();
  const areAnswersLocked = isAnnotationModeActive && !isSubmitted;
  const placedItem = wordBankItems.find((item) => item.placedInSlot === slot.id);
  const copy = useItemMoveCopy();
  const blankLabel = useBlankName(slot.label, index);
  const isActiveTarget = activeItemId !== null;
  const isEmptyBlank = !isSubmitted && !placedItem;
  const isFilledBlank = !isSubmitted && !!placedItem;
  const handleSlotClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent conflicts with item clicks
    e.stopPropagation();
    onClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const smallSupplement = slot.label ? getSmallSupplement(slot.label) : null;
  // An audio supplement swaps its caption for a full-width scrubber while playing; if the placed
  // chip is content-sized it collapses and the controls overlap. Let it fill the slot instead.
  const placedItemIsAudio = placedItem
    ? getSmallSupplement(placedItem.value)?.type === 'audio'
    : false;
  const valueRenderer = slot.label ? (
    <SupplementContent value={slot.label} className='rounded-none' />
  ) : null;

  const onClickEvent = useEffectEvent(() => onClick());

  useEffect(() => {
    const clickHandler = (e: MouseEvent) => {
      // only handle clicks if an item is active
      if (activeItemId === null) return;
      if (node.current && node.current.contains(e.target as Node)) {
        e.stopPropagation();
        onClickEvent();
      }
    };
    document.addEventListener('click', clickHandler);
    return () => {
      document.removeEventListener('click', clickHandler);
    };
  }, [node, activeItemId]);

  const isCorrect = useMemo(() => {
    const bankId = placedItem?.id;
    const slotId = slot.id;
    const slotAnswerId = correctAnswer ? correctAnswer[slotId] : null;
    if (!isSubmitted || slotAnswerId === null) return false;
    if (bankId === slotAnswerId) return true;
    if (!interchangeable || !placedItem) return false;
    const answerItem = wordBankItems.find((item) => item.id === slotAnswerId);
    return itemsShareValue(placedItem.value, answerItem?.value);
  }, [correctAnswer, slot, placedItem, isSubmitted, interchangeable, wordBankItems]);

  const correctAnswerForSlot = useMemo(() => {
    const correctAnswerId = correctAnswer ? correctAnswer[slot.id] : null;
    const correctAnswerItem = wordBankItems.find((item) => item.id === correctAnswerId);
    if (!correctAnswerItem) return null;
    return <SupplementContent value={correctAnswerItem.value} />;
  }, [correctAnswer, slot, wordBankItems]);

  const correctAnswerKey = correctAnswerForSlot && (
    <>
      <Text size='xs' color='secondary' className='mt-x1'>
        {gradedCopy.correctAnswerLabel}
      </Text>
      <div className='mt-x0-half border-border-primary min-w-x0 overflow-hidden rounded-lg border'>
        {correctAnswerForSlot}
      </div>
    </>
  );

  const valueRendererRef = useCallback(
    (el: HTMLDivElement | null) => registerValueRenderer?.(slot.id, el),
    [registerValueRenderer, slot.id]
  );

  const isPlacing = isOver || isActiveTarget;

  return (
    <div
      ref={setNodeRef}
      {...(isFilledBlank ? {role: 'group', 'aria-label': blankLabel} : {})}
      className={classNames(
        'h-auto! items-center',
        {
          'border-2! border-dashed border-selected background-subtle': isOver || isActiveTarget,
          'border-2! border-positive!': isSubmitted && isCorrect,
          'border-2! border-negative!': isSubmitted && !isCorrect
        },
        'rounded-x1 border border-solid border-border-primary background-primary',
        BLANK_FOCUS_RING_CLASS
      )}
    >
      <div
        {...(isSupplement(slot.label ?? '') && ANNOTATE_IGNORE_PROPS)}
        ref={valueRendererRef}
        className={classNames(
          'flex w-full items-center justify-center rounded-t-lg border-b border-border-primary',
          {
            'p-x2 text-center': !smallSupplement,
            'background-subtle': smallSupplement?.type !== 'passage'
          }
        )}
      >
        {valueRenderer}
      </div>
      <div
        {...ANNOTATE_IGNORE_PROPS}
        data-testid={`bank-items__slot-${slot.id}`}
        ref={(el) => {
          registerSlotEl(slot.id, el);
        }}
        {...(!isSubmitted ? {onClick: handleSlotClick} : {})}
        {...(isEmptyBlank
          ? {
              onKeyDown: handleKeyDown,
              role: 'button',
              tabIndex: 0,
              'aria-label': copy.emptyBlank(blankLabel),
              ...lockedControlProps(areAnswersLocked)
            }
          : {})}
        className={cx(
          'flex min-h-x8 w-full items-center justify-center p-x1! focus-visible:outline-none',
          {
            'cursor-pointer background-info-subtle': isPlacing,
            'animate-highlight-fade': isHighlighted,
            'text-center': !smallSupplement
          }
        )}
      >
        {placedItem && (
          <div
            className={cx('min-w-x0 flex max-w-full flex-col justify-center', {
              'text-center': !smallSupplement,
              'w-full': placedItemIsAudio
            })}
            onClick={(e) => e.stopPropagation()}
            role='presentation'
          >
            <MovableItem
              key={placedItem.id}
              item={placedItem}
              isActive={activeItemId === placedItem.id}
              activeItemId={activeItemId}
              isDragging={isDragging}
              onClick={(e) => {
                if (!isSubmitted) {
                  onItemClick(placedItem.id, e);
                }
              }}
              onReset={onResetItem}
              isCorrect={isCorrect}
              isSubmitted={isSubmitted}
              suppressDimming
            />
            {isSubmitted && !isCorrect && correctAnswerKey}
          </div>
        )}
        {!placedItem && isSubmitted && !isCorrect && (
          <div className='min-w-x0 flex max-w-full flex-col justify-center' role='presentation'>
            <span className='sr-only' {...ANNOTATE_IGNORE_PROPS}>
              {noAnswerText(gradedCopy)}
            </span>
            <Chip
              color='negative'
              size='default'
              noMargin
              className='border-border-primary border'
              aria-hidden
            >
              <Icon icon={['far', 'circle-xmark']} className='mr-x0-half' />
              No guess
            </Chip>
            {correctAnswerKey}
          </div>
        )}
        {!placedItem && !(isSubmitted && !isCorrect) && (
          <Text color={isPlacing ? 'brand' : 'tertiary'}>Place here</Text>
        )}
      </div>
    </div>
  );
};
